// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { useLedgerSync } from '../../hooks/useLedgerSync.js';
import {
  getAccountTransactions,
  computeRunningBalance,
  formatAmount,
  buildEvent,
} from '../../lib/eventLog.js';
import { INVESTMENT_ACCOUNT_TYPES } from '../../lib/investment.js';
import { MobileTopBar } from './MobileTopBar.js';
import { TransactionCard } from './TransactionCard.js';
import { PullToRefresh } from './PullToRefresh.js';
import { TransactionForm } from '../transactions/TransactionForm.js';
import type { Transaction, TransactionDeletedPayload } from '../../types.js';

interface MobileRegisterProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

const PAGE = 30; // Phase 4: paginate — never render the whole history at once.

export function MobileRegister({ worker, keyData }: MobileRegisterProps) {
  const { t } = useTranslation(['transactions', 'accounting', 'common', 'errors']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);
  const { sync } = useLedgerSync(worker, keyData);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [visible, setVisible] = useState(PAGE);
  const [topBalance, setTopBalance] = useState<number | null>(null);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [showForm, setShowForm] = useState(false);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollRef = useRef<HTMLDivElement>(null);

  const account = id ? ledger.accounts.get(id) : undefined;

  // Running balance is computed ascending, then we present most-recent-first.
  const { descTx, balanceMap } = useMemo(() => {
    if (!id) return { descTx: [] as Transaction[], balanceMap: new Map<string, number>() };
    const asc = getAccountTransactions(ledger, id);
    const withBal = computeRunningBalance(asc, id);
    const map = new Map(withBal.map(({ tx, runningBalance }) => [tx.id, runningBalance]));
    return { descTx: [...asc].reverse(), balanceMap: map };
  }, [ledger, id]);

  if (!id || !account) {
    return (
      <>
        <MobileTopBar title={t('common:nav.register')} back />
        <div className="flex-1 flex items-center justify-center text-text-muted p-6 text-center">
          {id ? t('register.accountNotFound') : t('register.selectFromAccountsTab')}
        </div>
      </>
    );
  }

  // Investment registers keep their specialized desktop view (out of scope for the
  // card redesign, which targets cash/bank/expense registers).
  const isInvestment = INVESTMENT_ACCOUNT_TYPES.has(account.type);

  const shown = descTx.slice(0, visible);

  // Track the topmost visible card to drive the sticky running-balance header.
  function handleScroll() {
    const container = scrollRef.current;
    if (!container) return;
    const top = container.getBoundingClientRect().top;
    let bestId: string | null = null;
    for (const tx of shown) {
      const el = cardRefs.current.get(tx.id);
      if (!el) continue;
      if (el.getBoundingClientRect().bottom > top + 4) {
        bestId = tx.id;
        break;
      }
    }
    if (bestId) setTopBalance(balanceMap.get(bestId) ?? null);
    // Infinite scroll: grow the window as we near the bottom.
    if (container.scrollHeight - container.scrollTop - container.clientHeight < 400) {
      setVisible((v) => (v < descTx.length ? v + PAGE : v));
    }
  }

  async function handleDelete(txId: string) {
    if (!confirm(t('register.confirmDelete'))) return;
    try {
      const payload: TransactionDeletedPayload = { transactionId: txId };
      await encryptAndPush(await buildEvent('TransactionDeleted', payload, ledger.lastSequence + 1));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('register.deleteFailed'));
    }
  }

  async function handleDuplicate(tx: Transaction) {
    const splits = tx.splits.map((s) => ({
      ...s,
      splitId: crypto.randomUUID(),
      reconcileState: 'n' as const,
      reconcileDate: null,
    }));
    try {
      await encryptAndPush(
        await buildEvent(
          'TransactionPosted',
          {
            transactionId: crypto.randomUUID(),
            date: new Date().toISOString().slice(0, 10),
            dateEntered: new Date().toISOString(),
            description: tx.description,
            num: tx.num,
            notes: tx.notes,
            currency: tx.currency,
            splits,
          },
          ledger.lastSequence + 1,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : t('errors:duplicate'));
    }
  }

  const displayBalance = topBalance ?? account.balance;

  return (
    <>
      <MobileTopBar
        title={account.name}
        back
        right={
          !account.placeholder && !isInvestment ? (
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="px-3 h-8 bg-accent text-white text-sm font-medium active:bg-accent-hover"
            >
              {t('common:actions.new')}
            </button>
          ) : undefined
        }
      />

      {/* Sticky running-balance header — reflects the topmost visible transaction */}
      <div className="shrink-0 bg-bg-surface border-b border-border px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wide text-text-muted">{t('accounting:columns.balance')}</span>
          {!account.placeholder && (
            <button
              onClick={() => navigate(`/reconcile/${id}`)}
              className="text-xs text-accent active:text-accent-hover"
            >
              {t('register.reconcile')}
            </button>
          )}
        </div>
        <span className={`text-md font-mono tabular-nums ${displayBalance < 0 ? 'text-negative' : 'text-text'}`}>
          {formatAmount(displayBalance, account.currency)}
        </span>
      </div>

      {isInvestment ? (
        <div className="flex-1 flex items-center justify-center p-6 text-center text-text-muted text-sm">
          {t('register.investmentDesktopOnly')}
        </div>
      ) : (
        <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto native-scroll pb-tabbar">
          <PullToRefresh onRefresh={sync} scrollRef={scrollRef} />
          {shown.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-text-muted">
              <p className="text-sm">{t('register.noTransactions')}</p>
              <button onClick={() => navigate('/quick-entry')} className="mt-2 text-accent text-sm">
                {t('register.addTransaction')}
              </button>
            </div>
          ) : (
            shown.map((tx) => (
              <div key={tx.id} ref={(el) => registerRef(cardRefs.current, tx.id, el)}>
                <TransactionCard
                  tx={tx}
                  accountId={id}
                  accounts={ledger.accounts}
                  currency={account.currency}
                  runningBalance={balanceMap.get(tx.id) ?? 0}
                  expanded={expandedId === tx.id}
                  onToggle={() => setExpandedId(expandedId === tx.id ? null : tx.id)}
                  onEdit={() => { setEditing(tx); setShowForm(true); }}
                  onDuplicate={() => void handleDuplicate(tx)}
                  onDelete={() => void handleDelete(tx.id)}
                  onNavigateAccount={(accId) => navigate(`/accounts/${accId}`)}
                />
              </div>
            ))
          )}
          {visible < descTx.length && (
            <div className="py-4 text-center text-xs text-text-subtle">{t('register.loadingOlder')}</div>
          )}
        </div>
      )}

      {showForm && (
        <TransactionForm
          defaultAccountId={id}
          {...(editing ? { editTransaction: editing } : {})}
          worker={worker}
          keyData={keyData}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </>
  );
}

function registerRef(map: Map<string, HTMLDivElement>, id: string, el: HTMLDivElement | null) {
  if (el) map.set(id, el);
  else map.delete(id);
}
